// The inspector's other card: the square itself, when nothing is standing on
// it — a vein, a heap of scrap, a belt (the contract's `GroundCard`).
//
// The same surface and the same parts as `Inspector.tsx`, because it is the
// same card: one home for a selection, whatever the selection turns out to be.
// **Few words**: a swatch labels the material and the headline is the number,
// so there is no caption on either.

import type { GroundCard as GroundView } from "src/api";
import { OWNER } from "./Inspector";
import { UnitChips } from "./unitRows";
import { holdingTone } from "./unitTone";
import { Under } from "./inspector/Under";
import { useCardBox } from "./inspector/useCardBox";
import {
  Aside,
  Card,
  Fill,
  Headline,
  Name,
  NameSwatch,
  Prose,
  Row,
  Track,
  Where
} from "./inspector/styles";

export function GroundCard({ card: model }: { card: GroundView }) {
  const { root, box } = useCardBox();

  return (
    <Card ref={root} data-testid="ground-card">
      <Name>
        {model.swatch !== null && (
          <NameSwatch style={{ background: holdingTone(model.swatch) }} />
        )}
        {model.name}
        <Where>
          {model.square}
          {model.owner !== null && ` · ${OWNER[model.owner]}`}
        </Where>
      </Name>

      {/* The seam, then this tile's own charge beside it — the number you spend
          a pawn's turns against, and the number you get for standing here. */}
      {model.count !== null && (
        <Headline data-testid="ground-count">
          {model.count}
          {model.here !== null && <Aside data-testid="ground-here">{model.here} here</Aside>}
        </Headline>
      )}

      {model.progress && (
        <Track data-testid="ground-dig">
          <Fill
            className={model.progress.ready ? "ready" : undefined}
            style={{ width: `${Math.round(model.progress.fraction * 100)}%` }}
          />
        </Track>
      )}

      {model.holdings.length > 0 && (
        <Row data-testid="ground-cargo">
          <UnitChips holdings={model.holdings} beside={box} />
        </Row>
      )}

      {model.under && <Under row={model.under} />}

      {/* The rule the square obeys, in the card and not on a hover — a card
          nobody can open on a touch screen is a rule nobody reads. */}
      {model.note !== null && <Prose data-testid="ground-note">{model.note}</Prose>}
    </Card>
  );
}
